// The session-history rail, and the project's notice line, supplied to
// `FocusFrame` out of band.
//
// `FocusFrame` mints the `.loom-focus` root on EVERY Focus screen (13 call
// sites across the wizard, chat, refine and implement), so a rail passed as a
// prop would have to be threaded through all of them. It rides a context
// instead: `FocusPage` provides the node once, per project, and every frame
// rendered underneath picks it up. A `null` value — the default, and what
// `/focus` (no project) provides — means no drawer and no toggle, so a screen
// outside a project renders exactly as it does without this file. The notice
// (the paying key's month against its limit) rides the same way, and a `null`
// one draws nothing.
//
// Its own module rather than `ui.tsx` so `FocusSessionRail` can import the ui
// primitives without an import cycle.

import { createContext, useContext, type ReactNode } from "react";

interface FocusRailValue {
  readonly rail: ReactNode;
  readonly notice: ReactNode;
}

const FocusRailContext = createContext<FocusRailValue>({ rail: null, notice: null });

export const FocusRailProvider = ({
  rail,
  notice = null,
  children,
}: {
  readonly rail: ReactNode;
  readonly notice?: ReactNode;
  readonly children: ReactNode;
}) => <FocusRailContext.Provider value={{ rail, notice }}>{children}</FocusRailContext.Provider>;

/** The rail node for the surrounding project, or `null` when there is none. */
export const useFocusRail = (): ReactNode => useContext(FocusRailContext).rail;

/** The surrounding project's notice line, or `null` when there is none. */
export const useFocusNotice = (): ReactNode => useContext(FocusRailContext).notice;
